import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import {useTranslation} from 'react-i18next';
import Dashboard from '../../components/Dashboard';
import React, {ReactElement, useMemo, useState} from 'react';
import TWithProviders, {
  MRT_ColumnDef_Add_Show,
  validateEmail,
  validateLengthMoreThanEqual,
  validateLoginType,
  validateRequired,
} from '../../components/MaterialTable';
import {
  createPlatformAddress,
  deleteManyPlatformAddress,
  getPlatformAddressList,
  updatePlatformAddress,
} from '../../api/platformAddress';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Alert,
  AlertColor,
  Chip,
  IconButton,
  Snackbar,
  SnackbarOrigin,
  Switch,
  Tooltip,
} from '@mui/material';
import {ALERT_HIDE_DURATION} from '../../constant';
import {CreatedAtColumn} from '../../components/Date/CreatedAtColumn';
import EditTableRoleColumn from '../../components/EditTable/EditTableRoleColumn';
import {usePlatform} from '../../context/platform';
import {MRT_Cell} from 'material-react-table';
import {usePlatformUsage} from '../../context/platformUsage';
import EditTableBooleanColumn from '../../components/EditTable/EditTableBooleanColumn';
import {ContentCopy, Visibility} from '@mui/icons-material';
import {PlatformUsageDto} from '../../api/dto/platform';
dayjs.extend(utc);

function validate(data: any) {
  return {
    address: !validateRequired(data.address) ? 'Address is Required' : '',
  };
}

interface State extends SnackbarOrigin {
  open: boolean;
  type: AlertColor;
  message: string;
  autoHideDuration: number;
}
function PlatformAddressContent(): ReactElement {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const {t} = useTranslation();
  const {platform} = usePlatform();
  const {platformUsage, updatePlatformUsage} = usePlatformUsage();

  const [state, setState] = React.useState<State>({
    open: false,
    type: 'success',
    message: '',
    vertical: 'bottom',
    horizontal: 'right',
    autoHideDuration: ALERT_HIDE_DURATION,
  });
  const {open} = state;

  const handleClick = (
    type: AlertColor,
    message: string,
    autoHideDuration?: number,
  ) => {
    setState({
      ...state,
      type,
      message,
      open: true,
      autoHideDuration: autoHideDuration || ALERT_HIDE_DURATION,
    });
  };

  const handleClose = () => {
    setState({...state, open: false});
  };

  const columns = useMemo<MRT_ColumnDef_Add_Show<any>[]>(() => {
    const filteredColumns = [
      {
        accessorKey: 'id',
        header: 'Id',
        enableEditing: false,
        show: false,
        size: 60,
        Cell: ({cell}: {cell: MRT_Cell<any>}) => {
          const [copied, setCopied] = useState(false);
          const idValue = cell.getValue<string>();

          const handleCopy = () => {
            navigator.clipboard.writeText(idValue);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          };

          return (
            <Tooltip
              title={
                copied
                  ? t('dashboardIdCopyTextResult')
                  : t('dashboardIdCopyText')
              }
              arrow
            >
              <IconButton onClick={handleCopy} size='small'>
                <Typography variant='body2' sx={{fontWeight: 800, mr: 1}}>
                  {idValue}
                </Typography>
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'address',
        header: t('addressHeader'),
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.address,
          helperText: validationErrors?.address,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              address: undefined,
            }),
        },
        Cell: ({cell}: {cell: MRT_Cell<any>}) => {
          const [copied, setCopied] = useState(false);
          const addressValue = cell.getValue<string>();

          // 短縮地址顯示，只顯示前8位和後8位
          const displayAddress =
            addressValue?.length > 16
              ? `${addressValue.substring(0, 8)}...${addressValue.substring(
                  addressValue.length - 8,
                )}`
              : addressValue;

          const handleCopy = () => {
            navigator.clipboard.writeText(addressValue);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          };

          return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <Typography variant='body2' sx={{fontWeight: 500}}>
                {displayAddress}
              </Typography>
              <Tooltip title={copied ? t('copied') : t('copyAddress')} arrow>
                <IconButton onClick={handleCopy} size='small'>
                  <ContentCopy fontSize='small' />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },

      {
        accessorKey: 'isEnable',
        header: t('isEnabledHeader'),
        show: true,
        editSelectOptions: ['true', 'false'],
        muiEditTextFieldProps: {
          select: true,
          defaultChecked: true,
          defaultValue: true,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              isEnabled: undefined,
            }),
        },
        Edit: ({cell, row, column}: any) => (
          <EditTableBooleanColumn
            label={t('dashboardTablePlatformAddressIsEnabled')}
            t={t}
            cell={cell}
            row={row}
            column={column}
          />
        ),
        Cell: ({cell}: {cell: MRT_Cell<any>}) => {
          return (
            <Switch
              checked={cell.getValue<boolean>()}
              disabled={true}
              inputProps={{'aria-label': 'controlled'}}
              color='primary'
            />
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: t('createdAtHeader'),
        enableEditing: false,
        Cell: ({cell}: any) => <CreatedAtColumn cell={cell} />,
      },
    ];

    return filteredColumns;
  }, [t, validationErrors]);

  return (
    <Container
      id='platformAddress'
      sx={{
        pt: {xs: 4, sm: 12},
        pb: {xs: 8, sm: 16},
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: {xs: 3, sm: 6},
      }}
    >
      <Box
        sx={{
          width: {sm: '100%', md: '60%'},
          textAlign: {sm: 'left', md: 'center'},
          display: 'flex',
          flexDirection: 'column',
          alignItems: {sm: 'flex-start', md: 'center'},
          gap: 1.5,
          py: 2,
        }}
      >
        <Typography
          component='h2'
          variant='h4'
          color='text.primary'
          fontWeight='bold'
        >
          {t('dashboardMenuPlatformAddress')}
        </Typography>
        <Typography
          variant='body1'
          color='text.secondary'
          sx={{maxWidth: '80%', opacity: 0.9}}
        >
          {t('dashboardTablePlatformAddressDescription')}
        </Typography>
      </Box>
      <Tooltip title={t('dashboardTableCreditTooltip')} arrow>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: '-1rem',
          }}
        >
          <Chip
            label={
              <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                <Typography variant='body2' color='primary'>
                  {platformUsage?.platformAddressCount}
                </Typography>
                /
                <Typography variant='body2' color='text.secondary'>
                  {platformUsage?.ruleObj.maxLimitPlatformAddressCount}
                </Typography>
              </Box>
            }
            size='small'
            color='default'
            sx={{
              fontSize: '0.75rem',
              height: 24,
              paddingX: 1.2,
              backgroundColor: 'background.paper',
              border: '1px solid #ccc',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          />
        </Box>
      </Tooltip>

      <TWithProviders
        props={{
          createApi: async (data: any) => {
            const response = await createPlatformAddress(data);
            if (platformUsage) {
              const updatedUsage: PlatformUsageDto = {
                ...platformUsage,
                platformAddressCount: platformUsage.platformAddressCount + 1,
              };
              updatePlatformUsage(updatedUsage);
            }
            return response;
          },
          updateApi: updatePlatformAddress,
          getListApi: getPlatformAddressList,
          // deleteApi: deletePlatformAddress,
          deleteBulkApi: async (ids: string[]) => {
            const response = await deleteManyPlatformAddress(ids);
            if (platformUsage) {
              const updatedUsage: PlatformUsageDto = {
                ...platformUsage,
                platformAddressCount:
                  platformUsage.platformAddressCount - ids.length,
              };
              updatePlatformUsage(updatedUsage);
            }
            return response;
          },
        }}
        columns={columns}
        validate={validate}
        handleClick={handleClick}
        setValidateErrors={setValidationErrors}
      ></TWithProviders>
      <Snackbar
        anchorOrigin={{vertical: state.vertical, horizontal: state.horizontal}}
        open={open}
        autoHideDuration={ALERT_HIDE_DURATION}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={state.type}
          variant='filled'
          sx={{width: '100%'}}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default function DashboardPlatformAddress() {
  return (
    <>
      <Dashboard element={<PlatformAddressContent />}></Dashboard>
    </>
  );
}
